/* eslint-disable no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch, store } from '../store';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import {
    hasAdsReportingFeatureAccess,
    isCurrentAdsReportingPractice,
    resolveAdsPracticeId,
    normalizeAdsPracticeId,
} from './adsReportingScope';
import { updateError } from './user';

const initialState = {
    isLoading: false,
    accounts: [],
    customers: [],
    availableCustomers: [],
    campaigns: [],
    summary: null,
    isSyncing: false,
    // Daily metrics
    dailyMetrics: [],
    dailyMetricsLoading: false,
    // Keywords
    keywords: [],
    keywordsLoading: false,
    // Active keywords — keywords that received prospect clicks in the period
    // (min_clicks=1). Kept in a SEPARATE field from `keywords` so the Overview
    // tab's compact widget never clobbers the full Keywords tab list (and vice
    // versa) when both are visited in a session.
    activeKeywords: [],
    activeKeywordsLoading: false,
    // Keyword ROI map — { [criterion_id]: { cost_per_attributed_lead,
    // attributed_leads } }. Merged onto keyword rows before render so the
    // table gets the cost/lead + attribution columns without changing the
    // existing /keywords/ endpoint response shape.
    keywordROIMap: {},
    // Keyword top-of-page bid map — { [lowercased keyword text]: { low_micros,
    // high_micros } } from the Keyword Planner endpoint. Merged onto rows by
    // keyword text. Empty until the backend (#5571) is deployed.
    keywordBidMap: {},
    // Spend-by-treatment rollup (per-practice): combined Google + Meta spend,
    // enquiries and cost-per-lead per treatment, for the Overview tab card.
    spendByTreatment: [],
    spendByTreatmentLoading: false,
    spendByTreatmentMeta: null,
    // Google Ads geo performance — clicks/impressions/spend by town/region
    // (geographic_view). Town-level (NOT postcode). Feeds the Ad Clicks map.
    geoPerformance: [],
    geoPerformanceLoading: false,
    // Period comparison
    comparison: null,
    comparisonLoading: false,
    // Cross-practice comparison
    crossPractice: null,
    crossPracticeLoading: false,
    // Cross-practice campaign benchmark — flat list of campaigns across the
    // user's GA practices (client-side fan-out), grouped by treatment in the UI.
    crossPracticeCampaigns: [],
    crossPracticeCampaignsLoading: false,
    // Keyword matrix for the currently-expanded treatment theme — flat keyword
    // rows tagged with practice, fanned out from the theme's campaigns.
    themeKeywords: [],
    themeKeywordsLoading: false,
    // Shared campaign→treatment mapping (backend model) — rows of
    // { id, campaign_id, practice_id, treatment }. Org-wide, reusable on any page.
    campaignTreatments: [],
    campaignTreatmentsLoading: false,
    // Cross-practice campaign data for BOTH comparison periods, so the
    // Cross-Practice Comparison can be re-scoped to a chosen campaign/type/status.
    crossPracticeCampaignCompare: [],
    crossPracticeCampaignCompareLoading: false,
    // Daily new-enquiry (lead) counts for the Daily Performance chart — sourced
    // from the existing analytics-reports endpoint, grouped by created day.
    // Practice-wide for now (NOT ad-campaign-attributed — deferred).
    // Rows: { created_at__date: 'YYYY-MM-DD', count_id: N }.
    dailyEnquiries: [],
    dailyEnquiriesLoading: false,
    // Conversion-measurement KPIs (contact actions + enquiries + calls) for the
    // selected range. Google Ads conversions come from `summary`; these are the
    // CRM-side measures pulled from their own endpoints.
    conversionMetrics: null,
    conversionMetricsLoading: false,
    // Per-conversion-action breakdown (Google Ads), live-queried + cached server-side.
    conversionActions: [],
    conversionActionsLoading: false,
    // Super-admin global campaign→treatment rules + the global treatment-item
    // catalog used by the rule editor's picker.
    campaignTreatmentRules: [],
    campaignTreatmentRulesLoading: false,
    treatmentItems: [],
};

const slice = createSlice({
    name: 'googleAds',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setAccounts(state, action) {
            state.accounts = action.payload;
        },
        updateConversionConfig(state, { payload: { accountId, customerId, config } }) {
            const values = {
                conversion_upload_enabled: config.conversion_upload_enabled,
                conversion_action_resource_name: config.conversion_action_resource_name,
            };
            const account = state.accounts.find((item) => String(item.id) === String(accountId));
            const nested = account?.customers?.find((item) => String(item.id) === String(customerId));
            if (nested) Object.assign(nested, values);
            const customer = state.customers.find((item) => String(item.id) === String(customerId));
            if (customer) Object.assign(customer, values);
        },
        setCustomers(state, action) {
            state.customers = action.payload;
        },
        setAvailableCustomers(state, action) {
            state.availableCustomers = action.payload;
        },
        setCampaigns(state, action) {
            state.campaigns = action.payload;
        },
        setSummary(state, action) {
            state.summary = action.payload;
        },
        setSyncing(state, action) {
            state.isSyncing = action.payload;
        },
        setDailyMetrics(state, action) {
            state.dailyMetrics = action.payload;
        },
        setDailyMetricsLoading(state, action) {
            state.dailyMetricsLoading = action.payload;
        },
        setKeywords(state, action) {
            state.keywords = action.payload;
        },
        setKeywordsLoading(state, action) {
            state.keywordsLoading = action.payload;
        },
        setActiveKeywords(state, action) {
            state.activeKeywords = action.payload;
        },
        setActiveKeywordsLoading(state, action) {
            state.activeKeywordsLoading = action.payload;
        },
        setKeywordROIMap(state, action) {
            state.keywordROIMap = action.payload || {};
        },
        setKeywordBidMap(state, action) {
            state.keywordBidMap = action.payload || {};
        },
        setComparison(state, action) {
            state.comparison = action.payload;
        },
        setComparisonLoading(state, action) {
            state.comparisonLoading = action.payload;
        },
        setCrossPractice(state, action) {
            state.crossPractice = action.payload;
        },
        setCrossPracticeLoading(state, action) {
            state.crossPracticeLoading = action.payload;
        },
        setCrossPracticeCampaigns(state, action) {
            state.crossPracticeCampaigns = action.payload;
        },
        setCrossPracticeCampaignsLoading(state, action) {
            state.crossPracticeCampaignsLoading = action.payload;
        },
        setThemeKeywords(state, action) {
            state.themeKeywords = action.payload;
        },
        setThemeKeywordsLoading(state, action) {
            state.themeKeywordsLoading = action.payload;
        },
        setCampaignTreatmentsList(state, action) {
            state.campaignTreatments = action.payload;
        },
        setCampaignTreatmentsLoading(state, action) {
            state.campaignTreatmentsLoading = action.payload;
        },
        setCrossPracticeCampaignCompare(state, action) {
            state.crossPracticeCampaignCompare = action.payload;
        },
        setCrossPracticeCampaignCompareLoading(state, action) {
            state.crossPracticeCampaignCompareLoading = action.payload;
        },
        setDailyEnquiries(state, action) {
            state.dailyEnquiries = action.payload;
        },
        setDailyEnquiriesLoading(state, action) {
            state.dailyEnquiriesLoading = action.payload;
        },
        setConversionMetrics(state, action) {
            state.conversionMetrics = action.payload;
        },
        setConversionMetricsLoading(state, action) {
            state.conversionMetricsLoading = action.payload;
        },
        setConversionActions(state, action) {
            state.conversionActions = action.payload;
        },
        setConversionActionsLoading(state, action) {
            state.conversionActionsLoading = action.payload;
        },
        setSpendByTreatment(state, action) {
            state.spendByTreatment = action.payload;
        },
        setSpendByTreatmentLoading(state, action) {
            state.spendByTreatmentLoading = action.payload;
        },
        setSpendByTreatmentMeta(state, action) {
            state.spendByTreatmentMeta = action.payload;
        },
        setGeoPerformance(state, action) {
            state.geoPerformance = action.payload;
        },
        setGeoPerformanceLoading(state, action) {
            state.geoPerformanceLoading = action.payload;
        },
        setCampaignTreatmentRules(state, action) {
            state.campaignTreatmentRules = action.payload || [];
        },
        setCampaignTreatmentRulesLoading(state, action) {
            state.campaignTreatmentRulesLoading = action.payload;
        },
        setTreatmentItems(state, action) {
            state.treatmentItems = action.payload || [];
        },
        reset() {
            return initialState;
        },
    },
});

export const { reset: resetGoogleAds } = slice.actions;

export default slice.reducer;

// --------------- Thunks ---------------

function getPracticeId() {
    const id = store.getState().practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
}

function getRequestScope(explicitPracticeId) {
    return resolveAdsPracticeId(explicitPracticeId, store.getState());
}

function hasScopeAccess(featureName, scope) {
    return scope.reportScoped
        ? hasAdsReportingFeatureAccess(featureName, scope.practiceId, store.getState())
        : hasFeatureAccess(featureName, scope.practiceId);
}

function isScopeCurrent(scope) {
    return !scope.reportScoped || isCurrentAdsReportingPractice(scope.practiceId, store.getState());
}

function dispatchForScope(scope, action) {
    if (isScopeCurrent(scope)) dispatch(action);
}

/**
 * Fetch all connected Google Ads accounts for this practice
 */
export function fetchGoogleAdsAccounts(explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/google-ads-accounts/`);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setAccounts(response.data?.results || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch Google Ads accounts' }));
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/**
 * Get the OAuth URL and redirect the user to Google's consent screen
 */
export function getAuthUrl(explicitPracticeId, { reportScoped = false, customerId = null } = {}) {
    return async () => {
        const practiceId = normalizeAdsPracticeId(explicitPracticeId ?? getPracticeId());
        const scope = { practiceId, reportScoped };
        if (!practiceId || !hasScopeAccess('google-ads', scope)) return false;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/google-ads-accounts/auth_url/`);
            const current = reportScoped ? isScopeCurrent(scope) : normalizeAdsPracticeId(getPracticeId()) === practiceId;
            if (!current) return false;
            if (response.status !== 200 || !response.data?.auth_url) throw new Error('authorization unavailable');
            // Older backends omit practice_id. The requested practice remains
            // explicit; never fall back to the global practice on callback.
            const responsePractice = response.data.practice_id === undefined
                ? practiceId : normalizeAdsPracticeId(response.data.practice_id);
            const url = new URL(response.data.auth_url);
            const state = url.searchParams.get('state');
            if (responsePractice !== practiceId || !state || url.origin !== 'https://accounts.google.com') {
                throw new Error('authorization mismatch');
            }
            sessionStorage.setItem('googleAdsOAuthIntent', JSON.stringify({
                state, practiceId, customerId, createdAt: Date.now(),
            }));
            window.location.href = url.href;
            return true;
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: 'Unable to start Google Ads connection. Please try again.' }));
            return false;
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/** Exchange a Google code only for this tab's original OAuth intent. */
export function exchangeAuthCode(code, state, onSuccess, onError) {
    return async () => {
        const failure = 'Google connection could not be completed. Reconnect and approve all requested permissions.';
        try {
            const intent = JSON.parse(sessionStorage.getItem('googleAdsOAuthIntent') || 'null');
            sessionStorage.removeItem('googleAdsOAuthIntent');
            const practiceId = normalizeAdsPracticeId(intent?.practiceId);
            const age = Date.now() - intent?.createdAt;
            if (!code || !state || intent?.state !== state || !practiceId || !Number.isFinite(age) || age < 0 || age > 600000) {
                throw new Error('authorization intent unavailable');
            }
            // The server validates the signed state and current user's access.
            // Global-practice feature snapshots may belong to a different
            // practice after returning from Google, so do not consult them.
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/callback/`,
                'post',
                { code, state }
            );
            if (response.status !== 200 && response.status !== 201) throw new Error('authorization rejected');
            dispatch(updateError({ success: true, message: 'Google Ads account connected successfully' }));
            if (onSuccess) onSuccess(response.data, intent);
        } catch (error) {
            dispatch(updateError({ success: false, message: failure }));
            if (onError) onError(failure);
        }
    };
}

/**
 * Fetch available customer accounts after OAuth connection
 */
export function fetchAvailableCustomers(accountId, explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/available_customers/`
            );
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setAvailableCustomers(response.data?.customers || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch available customers' }));
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/**
 * Connect selected customer accounts to start syncing campaigns
 */
export function connectCustomers(accountId, customerIds, explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/connect_customers/`,
                'post',
                { customer_ids: customerIds }
            );
            if (response.status === 200 || response.status === 201) {
                dispatchForScope(scope, updateError({ success: true, message: `Connected ${response.data?.count || customerIds.length} customer account(s)` }));
                // Re-fetch accounts to get updated customer list
                if (isScopeCurrent(scope)) dispatch(fetchGoogleAdsAccounts(explicitPracticeId));
            } else {
                dispatchForScope(scope, updateError({ success: false, message: 'Failed to connect customer accounts' }));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to connect customer accounts' }));
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/**
 * Disconnect a Google Ads account
 */
export function disconnectAccount(accountId, explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/disconnect/`,
                'post'
            );
            if (response.status === 200) {
                dispatchForScope(scope, updateError({ success: true, message: 'Google Ads account disconnected' }));
                dispatchForScope(scope, slice.actions.reset());
                if (isScopeCurrent(scope)) dispatch(fetchGoogleAdsAccounts(explicitPracticeId));
            } else {
                dispatchForScope(scope, updateError({ success: false, message: 'Failed to disconnect account' }));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to disconnect account' }));
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/**
 * Fetch connected customers for an account
 */
export function fetchCustomers(accountId, explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/`
            );
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setCustomers(response.data?.results || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch customers' }));
        }
    };
}

/**
 * Fetch campaigns for a specific customer
 */
export function fetchCampaigns(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setLoading(true));
            const queryParams = new URLSearchParams();
            if (params.status) queryParams.append('status', params.status);
            if (params.campaign_type) queryParams.append('campaign_type', params.campaign_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setCampaigns(response.data?.results || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch campaigns' }));
        } finally {
            dispatchForScope(scope, slice.actions.setLoading(false));
        }
    };
}

/**
 * Fetch campaign summary (aggregated metrics for ENABLED campaigns)
 */
export function fetchCampaignSummary(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            const queryParams = new URLSearchParams();
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/summary/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setSummary(response.data));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch campaign summary' }));
        }
    };
}

/**
 * Trigger a manual sync for a customer, then refresh data
 */
export function syncCustomer(accountId, customerId, dateParams = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setSyncing(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/sync/`,
                'post'
            );
            if (response.status === 200) {
                dispatchForScope(scope, updateError({ success: true, message: 'Sync started. Refreshing data...' }));
                // Wait for Celery task to complete, then re-fetch with current date filters
                await new Promise((resolve) => { setTimeout(resolve, 3000); });
                if (isScopeCurrent(scope)) {
                    dispatch(fetchCampaigns(accountId, customerId, dateParams, explicitPracticeId));
                    dispatch(fetchCampaignSummary(accountId, customerId, dateParams, explicitPracticeId));
                    dispatch(fetchCustomers(accountId, explicitPracticeId));
                }
            } else {
                dispatchForScope(scope, updateError({ success: false, message: 'Failed to trigger sync' }));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to sync' }));
        } finally {
            dispatchForScope(scope, slice.actions.setSyncing(false));
        }
    };
}

/**
 * Page through the daily-metrics endpoint until exhausted.
 *
 * One row per (campaign, date), and the backend paginator CLAMPS `limit` to
 * 500 (ExtendedLimitOffsetPagination.max_limit) — so a single high-limit
 * request still silently truncates long multi-campaign ranges (e.g. 12 months
 * × 3+ campaigns). Loop offset pages until a short page / no `next`.
 *
 * Returns the accumulated rows, or null when the FIRST page fails (so callers
 * can keep previous state rather than blanking the chart on a transient error).
 * Exported for the Conversion Funnel's per-customer fan-out.
 */
export async function fetchAllDailyMetricRows(baseUrl) {
    const PAGE = 500;
    const MAX_PAGES = 10; // 5,000 rows ≈ 13 campaigns × 365 days — sane ceiling
    const sep = baseUrl.includes('?') ? '&' : '?';
    const rows = [];
    for (let page = 0; page < MAX_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await callApi(`${baseUrl}${sep}limit=${PAGE}&offset=${page * PAGE}`);
        if (res.status !== 200) return page === 0 ? null : rows;
        const batch = res.data?.results || [];
        rows.push(...batch);
        if (batch.length < PAGE || !res.data?.next) break;
    }
    return rows;
}

/**
 * Fetch daily metrics for a specific customer
 */
export function fetchDailyMetrics(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setDailyMetricsLoading(true));
            const queryParams = new URLSearchParams();
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/daily-metrics/${queryString ? `?${queryString}` : ''}`;
            const rows = await fetchAllDailyMetricRows(url);
            if (rows !== null) {
                dispatchForScope(scope, slice.actions.setDailyMetrics(rows));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch daily metrics' }));
        } finally {
            dispatchForScope(scope, slice.actions.setDailyMetricsLoading(false));
        }
    };
}

/**
 * Fetch daily new-enquiry (lead) counts for the Daily Performance chart.
 *
 * Reuses the existing analytics-reports leads endpoint with drf_aggregates:
 * `group_by[created_at__date]&aggregate[Count]=id` returns one row per day —
 * `{ created_at__date: 'YYYY-MM-DD', count_id: N }` — in a single cached
 * request. No Google-Ads attribution yet (deferred): this is practice-wide new
 * leads created per day, so the `campaign_id` filter is intentionally ignored.
 *
 * Gated on `analytics-reports` (a separate feature from `google-ads`) — a
 * practice without it simply gets an empty Enquiries series rather than a 403.
 */
export function fetchDailyEnquiries(params = {}, hourly = false, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId) return;
        if (!hasScopeAccess('analytics-reports', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setDailyEnquiriesLoading(true));
            // Build the query manually with RAW brackets to match the proven
            // drf_aggregates call shape elsewhere in the app (URLSearchParams
            // percent-encodes `[`/`]`). group_by buckets leads by created day;
            // aggregate counts ids → one { created_at__date, count_id } per day.
            // For ≤24h ranges, bucket by hour-of-day instead (created_at__hour →
            // 0-23) so the chart isn't a single useless daily point.
            let query = hourly
                ? '?group_by[created_at__hour]&aggregate[Count]=id'
                : '?group_by[created_at__date]&aggregate[Count]=id';
            // Full-day datetime bounds: created_at is a datetime, so a bare date
            // `__lte` means "<= midnight" and drops same-day leads (a 24h window
            // would show 0). Pin to start-of-day / end-of-day to count the end day.
            if (params.date_after) query += `&created_at__gte=${params.date_after}T00:00:00`;
            if (params.date_before) query += `&created_at__lte=${params.date_before}T23:59:59`;
            const url = `/api/v1/practices/${practiceId}/reports/leads/${query}`;
            const response = await callApi(url);
            if (response.status === 200) {
                // drf_aggregates wraps rows in a { data: [...] } envelope; keep a
                // bare-array fallback in case that ever changes.
                const rows = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                dispatchForScope(scope, slice.actions.setDailyEnquiries(rows));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch daily enquiries' }));
        } finally {
            dispatchForScope(scope, slice.actions.setDailyEnquiriesLoading(false));
        }
    };
}

/**
 * Fetch CRM-side conversion-measurement KPIs for the conversion cards row:
 *   - phone-number clicks + WhatsApp clicks (website telemetry, analytics-events)
 *   - new enquiries + a by-source breakdown (reports/leads group-by)
 *   - inbound Twilio calls (fan-out over the practice's numbers)
 *
 * Google Ads conversions are NOT fetched here — they already live on `summary`.
 * Every leg is best-effort (a missing feature / integration contributes 0 and,
 * for calls, flips `callsAvailable`) so one gap never blanks the whole row.
 */
export function fetchConversionMetrics(params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId) return;
        const { date_after: after, date_before: before } = params;
        // analytics-events: count via the paginated `count` (date filter is on a
        // DateField-style lookup, inclusive of the end day).
        const eventDateQ = `${after ? `&created_at__date__gte=${after}` : ''}${before ? `&created_at__date__lte=${before}` : ''}`;
        const countEvents = async (name) => {
            try {
                const res = await callApi(`/api/v1/practices/${practiceId}/analytics-events/?name=${name}&limit=1${eventDateQ}`);
                return res.status === 200 ? (res.data?.count || 0) : 0;
            } catch { return 0; }
        };

        const fetchEnquiriesBySource = async () => {
            try {
                // Full-day datetime bounds — created_at is a datetime (see fetchDailyEnquiries).
                let q = '?group_by[lead_source__type]&aggregate[Count]=id';
                if (after) q += `&created_at__gte=${after}T00:00:00`;
                if (before) q += `&created_at__lte=${before}T23:59:59`;
                const res = await callApi(`/api/v1/practices/${practiceId}/reports/leads/${q}`);
                const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                const bySource = rows
                    .map((r) => ({ source: r.lead_source__type || 'Unknown', count: r.count_id || 0 }))
                    .sort((a, b) => b.count - a.count);
                return { total: bySource.reduce((s, r) => s + r.count, 0), bySource };
            } catch { return { total: 0, bySource: [] }; }
        };

        const fetchCalls = async () => {
            try {
                const phonesRes = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/?limit=100`);
                if (phonesRes.status !== 200) return { count: 0, available: false };
                const phones = phonesRes.data?.results || phonesRes.data || [];
                if (!phones.length) return { count: 0, available: false };
                // created_at (call time, not last-mutation) with FULL-DAY datetime
                // bounds — a bare-date `__lte` on a datetime stops at midnight,
                // dropping every call on the range's final day (and reading 0 for
                // single-day ranges like "Last 24 Hours").
                const reqs = phones.map((p) => callApi(
                    `/api/v1/practices/${practiceId}/practice-twilio-phones/${p.id}/calls/?limit=1${after ? `&created_at__gte=${after}T00:00:00` : ''}${before ? `&created_at__lte=${before}T23:59:59` : ''}`
                ));
                const settled = await Promise.allSettled(reqs);
                let count = 0;
                settled.forEach((r) => {
                    if (r.status === 'fulfilled' && r.value?.status === 200) count += (r.value.data?.count || 0);
                });
                return { count, available: true };
            } catch { return { count: 0, available: false }; }
        };

        try {
            dispatchForScope(scope, slice.actions.setConversionMetricsLoading(true));
            const [phoneClicks, whatsappClicks, enquiries, calls] = await Promise.all([
                countEvents('practice_phone_number_clicked'),
                countEvents('practice_whatsapp_button_clicked'),
                fetchEnquiriesBySource(),
                fetchCalls(),
            ]);
            dispatchForScope(scope, slice.actions.setConversionMetrics({
                phoneClicks,
                whatsappClicks,
                enquiries: enquiries.total,
                enquiriesBySource: enquiries.bySource,
                calls: calls.count,
                callsAvailable: calls.available,
            }));
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch conversion metrics' }));
        } finally {
            dispatchForScope(scope, slice.actions.setConversionMetricsLoading(false));
        }
    };
}

/**
 * Fetch the per-conversion-action breakdown (Google Ads), account-wide for the
 * date range. Live server-side query (cached) — see the dentalcrm
 * /campaigns/conversion-actions/ endpoint. Best-effort + silent: until that
 * endpoint is deployed it 404s, and we don't want a global error toast on every
 * Overview load, so failures just leave the list empty (the table hides itself).
 */
export function fetchConversionActions(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setConversionActionsLoading(true));
            const qp = new URLSearchParams();
            if (params.date_after) qp.append('date_after', params.date_after);
            if (params.date_before) qp.append('date_before', params.date_before);
            const qs = qp.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/conversion-actions/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setConversionActions(response.data?.results || []));
            } else {
                dispatchForScope(scope, slice.actions.setConversionActions([]));
            }
        } catch (error) {
            dispatchForScope(scope, slice.actions.setConversionActions([]));
        } finally {
            dispatchForScope(scope, slice.actions.setConversionActionsLoading(false));
        }
    };
}

export function fetchGeoPerformance(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setGeoPerformanceLoading(true));
            const qp = new URLSearchParams();
            if (params.date_after) qp.append('date_after', params.date_after);
            if (params.date_before) qp.append('date_before', params.date_before);
            const qs = qp.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/geo-performance/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setGeoPerformance(response.data?.results || []));
            } else {
                dispatchForScope(scope, slice.actions.setGeoPerformance([]));
            }
        } catch (error) {
            dispatchForScope(scope, slice.actions.setGeoPerformance([]));
        } finally {
            dispatchForScope(scope, slice.actions.setGeoPerformanceLoading(false));
        }
    };
}

export function fetchSpendByTreatment(params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId) return;
        // The page uses date_after/date_before; this reports endpoint takes
        // start/end (YYYY-MM-DD) — map them.
        const { date_after: after, date_before: before } = params;
        try {
            dispatchForScope(scope, slice.actions.setSpendByTreatmentLoading(true));
            const q = new URLSearchParams();
            if (after) q.append('start', after);
            if (before) q.append('end', before);
            const qs = q.toString();
            const url = `/api/v1/practices/${practiceId}/reports/spend-by-treatment/${qs ? `?${qs}` : ''}`;
            const res = await callApi(url);
            if (res.status === 200) {
                dispatchForScope(scope, slice.actions.setSpendByTreatment(res.data?.data || []));
                dispatchForScope(scope, slice.actions.setSpendByTreatmentMeta(res.data?.meta || null));
            } else {
                // Clear meta too — stale meta from a previous range would keep the
                // card visible (it hides on `!meta`) with last range's banner state.
                dispatchForScope(scope, slice.actions.setSpendByTreatment([]));
                dispatchForScope(scope, slice.actions.setSpendByTreatmentMeta(null));
            }
        } catch (e) {
            dispatchForScope(scope, slice.actions.setSpendByTreatment([]));
            dispatchForScope(scope, slice.actions.setSpendByTreatmentMeta(null));
        } finally {
            dispatchForScope(scope, slice.actions.setSpendByTreatmentLoading(false));
        }
    };
}

// ----- Super-admin: global campaign→treatment rules -----

const CAMPAIGN_RULES_URL = '/api/v1/google-ads/campaign-treatment-rules/';

/** Fetch all global campaign→treatment rules (super-admin only). */
export function fetchCampaignTreatmentRules() {
    return async () => {
        try {
            dispatch(slice.actions.setCampaignTreatmentRulesLoading(true));
            const response = await callApi(CAMPAIGN_RULES_URL);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignTreatmentRules(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setCampaignTreatmentRules([]));
        } finally {
            dispatch(slice.actions.setCampaignTreatmentRulesLoading(false));
        }
    };
}

/** Fetch the global TreatmentItem catalog for the rule editor's picker. */
export function fetchTreatmentItems() {
    return async () => {
        try {
            const response = await callApi('/api/v1/treatment-items/?ordering=name&limit=1000');
            if (response.status === 200) {
                dispatch(slice.actions.setTreatmentItems(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setTreatmentItems([]));
        }
    };
}

/** Create or update a rule (PATCH when id supplied), then refresh the list. */
export function saveCampaignTreatmentRule(payload, id, onDone) {
    return async () => {
        try {
            const url = id ? `${CAMPAIGN_RULES_URL}${id}/` : CAMPAIGN_RULES_URL;
            const response = await callApi(url, id ? 'patch' : 'post', payload);
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: id ? 'Rule updated' : 'Rule created' }));
                dispatch(fetchCampaignTreatmentRules());
                if (onDone) onDone(true);
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || response.data?.error || 'Failed to save rule' }));
                if (onDone) onDone(false);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save rule' }));
            if (onDone) onDone(false);
        }
    };
}

/** Delete a rule, then refresh the list. */
export function deleteCampaignTreatmentRule(id, onDone) {
    return async () => {
        try {
            const response = await callApi(`${CAMPAIGN_RULES_URL}${id}/`, 'delete');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Rule deleted' }));
                dispatch(fetchCampaignTreatmentRules());
                if (onDone) onDone(true);
            } else if (onDone) {
                onDone(false);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete rule' }));
            if (onDone) onDone(false);
        }
    };
}

/** Re-run all rules now (rebuild AUTO tags across every practice). */
export function reapplyCampaignTreatmentRules(onDone) {
    return async () => {
        try {
            const response = await callApi(`${CAMPAIGN_RULES_URL}reapply/`, 'post', {});
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: `Applied — ${response.data?.applied ?? 0} campaign tags updated` }));
                if (onDone) onDone(true);
            } else if (onDone) {
                onDone(false);
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to re-apply rules' }));
            if (onDone) onDone(false);
        }
    };
}

/**
 * Fetch period comparison for daily metrics
 */
export function fetchComparison(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setComparisonLoading(true));
            const queryParams = new URLSearchParams();
            if (params.period_a_start) queryParams.append('period_a_start', params.period_a_start);
            if (params.period_a_end) queryParams.append('period_a_end', params.period_a_end);
            if (params.period_b_start) queryParams.append('period_b_start', params.period_b_start);
            if (params.period_b_end) queryParams.append('period_b_end', params.period_b_end);
            // Was silently dropped even when callers passed it — the compare
            // endpoint has supported this param all along
            // (apps/google_ads/views.py: campaign_id = request.query_params.get(...)),
            // but this thunk only ever forwarded the 4 date params, so the
            // top-level Compare Periods KPI strip could never be scoped to a
            // single campaign — it always aggregated every campaign on the
            // account regardless of any campaign/treatment selection made
            // elsewhere on the page.
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/daily-metrics/compare/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setComparison(response.data));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch comparison data' }));
        } finally {
            dispatchForScope(scope, slice.actions.setComparisonLoading(false));
        }
    };
}

/**
 * Fetch keyword performance data for a specific customer
 */
export function fetchKeywords(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setKeywordsLoading(true));
            const queryParams = new URLSearchParams();
            if (params.keyword) queryParams.append('keyword', params.keyword);
            if (params.match_type) queryParams.append('match_type', params.match_type);
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            if (params.min_clicks) queryParams.append('min_clicks', params.min_clicks);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const queryString = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keywords/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setKeywords(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch keywords' }));
        } finally {
            dispatchForScope(scope, slice.actions.setKeywordsLoading(false));
        }
    };
}

/**
 * Fetch ACTIVE keywords for the Overview widget — keywords that received at
 * least one prospect click in the period (min_clicks=1), so we surface what's
 * actually spending rather than the full keyword list. Stored separately from
 * `keywords` so it never collides with the full Keywords tab.
 */
export function fetchActiveKeywords(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setActiveKeywordsLoading(true));
            const queryParams = new URLSearchParams();
            // min_clicks=1 → only keywords that drew prospect clicks (i.e. active).
            queryParams.append('min_clicks', params.min_clicks ?? 1);
            queryParams.append('ordering', params.ordering || '-clicks');
            if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
            if (params.date_after) queryParams.append('date_after', params.date_after);
            if (params.date_before) queryParams.append('date_before', params.date_before);
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keywords/?${queryParams.toString()}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatchForScope(scope, slice.actions.setActiveKeywords(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to fetch active keywords' }));
        } finally {
            dispatchForScope(scope, slice.actions.setActiveKeywordsLoading(false));
        }
    };
}

/**
 * Set a campaign's status to PAUSED or ENABLED. SUPERADMIN-ONLY at the API
 * level (backend returns 403 otherwise). Mutates the advertiser's Google
 * Ads account, so the callsite should also gate the button on
 * `useAuth().user.is_superuser`. Returns true on success; false on any
 * error (reason surfaced via updateError).
 */
export function setCampaignStatus(accountId, customerId, campaignPk, newStatus, dateParams = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId || !campaignPk) return false;
        if (!hasScopeAccess('google-ads', scope)) return false;
        if (!['PAUSED', 'ENABLED'].includes(newStatus)) {
            dispatchForScope(scope, updateError({ success: false, message: 'Invalid status — must be PAUSED or ENABLED.' }));
            return false;
        }
        try {
            // callApi is positional: (uri, method, data, isUnAuthorized, options).
            // The earlier object-form invocation was silently broken — data
            // was never sent, and method coerced to a garbage string.
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/campaigns/${campaignPk}/set-status/`;
            const response = await callApi(url, 'POST', { status: newStatus });
            if (response.status === 200) {
                // Refresh campaigns list so the status badge updates.
                // Forward the caller's dateParams so the user's date filter
                // isn't silently wiped after every toggle.
                if (isScopeCurrent(scope)) dispatch(fetchCampaigns(accountId, customerId, dateParams || {}, explicitPracticeId));
                return true;
            }
            // 401 here means either the CRM JWT couldn't refresh (rare —
            // callApi auto-refreshes once) OR Google rejected the upstream
            // OAuth token. Treat as reconnect-required since that's the
            // only actionable recovery path for the user.
            const fallbackByStatus = {
                403: 'Only superadmins can change campaign status.',
                401: 'Google Ads account needs reconnecting.',
                429: 'Google Ads is rate-limited — try again in a few minutes.',
                502: 'Google Ads is temporarily unavailable — try again shortly.',
            };
            const msg = response.data?.error
                || fallbackByStatus[response.status]
                || 'Failed to change campaign status.';
            dispatchForScope(scope, updateError({ success: false, message: msg }));
            return false;
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to change campaign status.' }));
            return false;
        }
    };
}

/**
 * Toggle offline conversion upload for a customer. SUPERADMIN-ONLY at the
 * API level. Writing conversions to Google Ads affects Smart Bidding +
 * billing optimisation, so this is gated tighter than pause/resume.
 *
 * Args:
 *     enabled: boolean
 *     conversionActionResourceName: "customers/{cid}/conversionActions/{caid}"
 *         — required when enabling. Advertiser must create the
 *         conversion action in the Google Ads UI first and paste the
 *         resource name into our superadmin panel.
 */
export function setConversionUploadConfig(accountId, customerId, {
    enabled,
    conversionActionResourceName,
}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return false;
        if (!hasScopeAccess('google-ads', scope)) return false;
        try {
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/conversion-upload-config/`;
            const response = await callApi(url, 'POST', {
                enabled,
                conversion_action_resource_name: conversionActionResourceName || '',
            });
            if (!isScopeCurrent(scope) || !hasScopeAccess('google-ads', scope)
                || String(getRequestScope(explicitPracticeId).practiceId) !== String(practiceId)) return false;
            if (response.status === 200
                && typeof response.data?.conversion_upload_enabled === 'boolean'
                && typeof response.data?.conversion_action_resource_name === 'string') {
                dispatchForScope(scope, slice.actions.updateConversionConfig({ accountId, customerId, config: response.data }));
                return response.data;
            }
            const fallbackByStatus = {
                403: 'Only superadmins can toggle conversion upload.',
                400: response.data?.error || 'Missing conversion action resource name.',
            };
            const msg = response.data?.error
                || fallbackByStatus[response.status]
                || 'Failed to update conversion upload config.';
            dispatchForScope(scope, updateError({ success: false, message: msg }));
            return false;
        } catch (error) {
            dispatchForScope(scope, updateError({ success: false, message: error.message || 'Failed to update conversion upload config.' }));
            return false;
        }
    };
}

/**
 * Fetch keyword ROI — joins keyword spend against attributed leads via
 * utm_campaign match. Separate endpoint from /keywords/ because it's a
 * compute-heavier query; we only call it when the Keywords tab is open.
 * Returns rows with { cost_per_attributed_lead, attributed_leads }
 * fields which the KeywordsTable column set consumes. Response is an
 * array — not paginated — because the full keyword set is already
 * bounded by the backend sync cap.
 */
export function fetchKeywordROI(accountId, customerId, params = {}, explicitPracticeId = null) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            dispatchForScope(scope, slice.actions.setKeywordsLoading(true));
            const query = new URLSearchParams();
            if (params.start) query.append('start', params.start);
            if (params.end) query.append('end', params.end);
            const qs = query.toString();
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keyword-roi/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                // Merge ROI fields onto existing keywords by criterion_id so
                // the table keeps existing columns and just gains the new ones.
                const roi = response.data || [];
                dispatchForScope(scope, slice.actions.setKeywordROIMap(
                    Object.fromEntries(
                        roi.map((r) => [String(r.keyword_id), {
                            cost_per_attributed_lead: r.cost_per_attributed_lead,
                            attributed_leads: r.attributed_leads,
                        }])
                    )
                ));
            } else if (response.status !== 404) {
                // 404 = backend doesn't have the endpoint yet (old deploy).
                // Silently skip rather than showing an error banner.
                dispatchForScope(scope, updateError({ success: false, message: response.data?.error || 'Failed to load keyword ROI' }));
            }
        } catch (error) {
            // Network-layer failure (timeout, offline, DNS) — ROI is
            // supplementary; the keyword table still renders without it,
            // so we deliberately don't show an error banner. Capture to
            // Sentry so support has telemetry when a user reports a
            // missing ROI column but the UI stays quiet.
            // eslint-disable-next-line no-console
            console.warn('[google_ads] keyword ROI fetch failed:', error?.message);
            try {
                // Lazy import so the slice doesn't load Sentry on first paint.
                const Sentry = await import('@sentry/react');
                Sentry.captureException(error, {
                    tags: { area: 'google_ads', feature: 'keyword_roi' },
                });
            } catch {
                // Sentry unavailable — best effort only.
            }
        } finally {
            dispatchForScope(scope, slice.actions.setKeywordsLoading(false));
        }
    };
}

/**
 * Fetch top-of-page bid estimates (Keyword Planner) for the customer's keywords.
 * Returns { "<keyword lower>": {low_micros, high_micros} }, merged onto rows by
 * keyword text. Best-effort + silent: 404s until the backend (#5571) deploys, so
 * the bid column just shows "—" rather than erroring.
 */
export function fetchKeywordBidEstimates(accountId, customerId, explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const { practiceId } = scope;
        if (!practiceId || !accountId || !customerId) return;
        if (!hasScopeAccess('google-ads', scope)) return;
        try {
            const url = `/api/v1/practices/${practiceId}/google-ads-accounts/${accountId}/customers/${customerId}/keywords/bid-estimates/`;
            const response = await callApi(url);
            dispatchForScope(scope, slice.actions.setKeywordBidMap(
                response.status === 200 ? (response.data?.results || {}) : {}
            ));
        } catch (error) {
            dispatchForScope(scope, slice.actions.setKeywordBidMap({}));
        }
    };
}

/**
 * Fetch cross-practice comparison (top-level endpoint, no practice ID)
 */
/**
 * Cross-practice campaign benchmark via client-side fan-out: for each supplied
 * practice (those with a connected Google Ads account), resolve account →
 * customer → campaigns and flatten into one list tagged with practice info.
 * Practices that error or have no GA are skipped silently (best-effort). Used
 * by the "Campaign Benchmark" pivot, which groups these by a user-assigned
 * treatment theme. Heavy with many practices — only call on demand.
 *
 * `practices`: [{ id, name }] (e.g. from the cross-practice compare response).
 */
export function fetchCrossPracticeCampaigns({ practices = [], dateAfter, dateBefore } = {}) {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        dispatch(slice.actions.setCrossPracticeCampaignsLoading(true));
        try {
            const all = [];
            await Promise.all(practices.map(async (p) => {
                const pid = p.id || p.practice_id;
                const pname = p.name || p.practice_name;
                if (!pid) return;
                try {
                    const accRes = await callApi(`/api/v1/practices/${pid}/google-ads-accounts/`);
                    const account = (accRes.data?.results || accRes.data || [])[0];
                    if (!account?.id) return;
                    const custRes = await callApi(`/api/v1/practices/${pid}/google-ads-accounts/${account.id}/customers/`);
                    const customers = custRes.data?.results || custRes.data || [];
                    const customer = customers.find((c) => c.is_active) || customers[0];
                    if (!customer?.id) return;
                    const qp = new URLSearchParams();
                    if (dateAfter) qp.append('date_after', dateAfter);
                    if (dateBefore) qp.append('date_before', dateBefore);
                    const qs = qp.toString();
                    const campRes = await callApi(`/api/v1/practices/${pid}/google-ads-accounts/${account.id}/customers/${customer.id}/campaigns/${qs ? `?${qs}` : ''}`);
                    const camps = campRes.data?.results || campRes.data || [];
                    camps.forEach((c) => all.push({
                        practiceId: pid,
                        practiceName: pname,
                        accountId: account.id,
                        customerId: customer.id,
                        campaign_id: c.campaign_id || c.id,
                        name: c.name,
                        status: c.status,
                        campaign_type: c.campaign_type,
                        cost: parseFloat(c.cost || 0),
                        clicks: Number(c.clicks || 0),
                        impressions: Number(c.impressions || 0),
                        cpc: parseFloat(c.cpc || 0),
                        ctr: parseFloat(c.ctr || 0),
                    }));
                } catch (_) { /* skip practices that error / have no GA */ }
            }));
            dispatch(slice.actions.setCrossPracticeCampaigns(all));
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to load cross-practice campaigns' }));
        } finally {
            dispatch(slice.actions.setCrossPracticeCampaignsLoading(false));
        }
    };
}

/**
 * Keyword matrix for a treatment theme: fan out a keyword fetch per campaign
 * (across practices) mapped to that theme, flattened with practice info. Powers
 * the benchmark's drill-down — "are practices bidding on the same keywords for
 * Braces, and what's each paying per click?". Pass the theme's campaign rows
 * (each carrying practiceId/accountId/customerId/campaign_id from the campaign
 * fan-out). Best-effort: campaigns that error are skipped.
 */
export function fetchThemeKeywords({ campaigns = [], dateAfter, dateBefore } = {}) {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        dispatch(slice.actions.setThemeKeywordsLoading(true));
        try {
            const all = [];
            await Promise.all(campaigns.map(async (c) => {
                if (!c.practiceId || !c.accountId || !c.customerId || !c.campaign_id) return;
                try {
                    const qp = new URLSearchParams();
                    qp.append('campaign_id', c.campaign_id);
                    if (dateAfter) qp.append('date_after', dateAfter);
                    if (dateBefore) qp.append('date_before', dateBefore);
                    const url = `/api/v1/practices/${c.practiceId}/google-ads-accounts/${c.accountId}/customers/${c.customerId}/keywords/?${qp.toString()}`;
                    const res = await callApi(url);
                    const kws = res.data?.results || res.data || [];
                    kws.forEach((k) => all.push({
                        practiceId: c.practiceId,
                        practiceName: c.practiceName,
                        campaign_id: c.campaign_id,
                        keyword_text: k.keyword_text || k.text || '',
                        match_type: k.match_type,
                        clicks: Number(k.clicks || 0),
                        cost: parseFloat(k.cost || 0),
                        cpc: parseFloat(k.cpc || k.average_cpc || 0),
                        impressions: Number(k.impressions || 0),
                    }));
                } catch (_) { /* skip campaigns that error */ }
            }));
            dispatch(slice.actions.setThemeKeywords(all));
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to load keyword comparison' }));
        } finally {
            dispatch(slice.actions.setThemeKeywordsLoading(false));
        }
    };
}

/**
 * Load the shared campaign→treatment mapping (backend model). Org-wide, scoped
 * server-side to the practices the user can access. Fails silently if the
 * endpoint isn't deployed yet — the benchmark falls back to legacy per-user
 * preferences in that case.
 */
export function fetchCampaignTreatments() {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        dispatch(slice.actions.setCampaignTreatmentsLoading(true));
        try {
            const res = await callApi('/api/v1/google-ads/campaign-treatments/');
            if (res.status === 200) {
                dispatch(slice.actions.setCampaignTreatmentsList(res.data?.results || res.data || []));
            }
        } catch (_) { /* endpoint may not be deployed yet — prefs fallback handles it */ }
        finally {
            dispatch(slice.actions.setCampaignTreatmentsLoading(false));
        }
    };
}

/**
 * Replace a campaign's treatment tags on the shared backend model. Best-effort:
 * swallows errors so a pre-deploy 404 doesn't surface a toast (the per-user
 * preference mirror guarantees persistence until the backend is live).
 */
export function saveCampaignTreatments(campaignId, treatments) {
    return async () => {
        if (!hasFeatureAccess('google-ads') || !campaignId) return;
        try {
            await callApi('/api/v1/google-ads/campaign-treatments/set/', 'POST', {
                campaign_id: campaignId,
                treatments,
            });
        } catch (_) { /* pre-deploy fallback: per-user prefs persist the map */ }
    };
}

/**
 * Cross-practice campaign data for BOTH comparison periods via client-side
 * fan-out, so the Cross-Practice Comparison can be re-scoped to specific
 * campaigns/types/statuses. For each practice resolves account → customer,
 * fetches campaigns for period A and period B, and merges per campaign into
 * { practiceId, practiceName, campaign_id, name, status, campaign_type,
 *   a:{cost,clicks,impressions,conversions}, b:{...} }. On demand (only when a
 * filter is active) — it's a 2× fan-out.
 */
export function fetchCrossPracticeCampaignCompare({ practices = [], periodA, periodB } = {}) {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        dispatch(slice.actions.setCrossPracticeCampaignCompareLoading(true));
        try {
            const metrics = (c) => ({
                cost: parseFloat(c.cost || 0),
                clicks: Number(c.clicks || 0),
                impressions: Number(c.impressions || 0),
                conversions: parseFloat(c.conversions || 0),
            });
            const fetchCampaigns = async (pid, aid, cid, period) => {
                const qp = new URLSearchParams();
                if (period?.start) qp.append('date_after', period.start);
                if (period?.end) qp.append('date_before', period.end);
                const res = await callApi(`/api/v1/practices/${pid}/google-ads-accounts/${aid}/customers/${cid}/campaigns/?${qp.toString()}`);
                return res.data?.results || res.data || [];
            };
            const rows = [];
            await Promise.all(practices.map(async (p) => {
                const pid = p.id || p.practice_id;
                const pname = p.name || p.practice_name;
                if (!pid) return;
                try {
                    const account = (await callApi(`/api/v1/practices/${pid}/google-ads-accounts/`)).data?.results?.[0]
                        || (await callApi(`/api/v1/practices/${pid}/google-ads-accounts/`)).data?.[0];
                    if (!account?.id) return;
                    const custRes = await callApi(`/api/v1/practices/${pid}/google-ads-accounts/${account.id}/customers/`);
                    const customers = custRes.data?.results || custRes.data || [];
                    const customer = customers.find((c) => c.is_active) || customers[0];
                    if (!customer?.id) return;
                    const [aCamps, bCamps] = await Promise.all([
                        fetchCampaigns(pid, account.id, customer.id, periodA),
                        fetchCampaigns(pid, account.id, customer.id, periodB),
                    ]);
                    const byId = {};
                    aCamps.forEach((c) => {
                        const id = c.campaign_id || c.id;
                        byId[id] = {
                            practiceId: pid, practiceName: pname,
                            campaign_id: id, name: c.name, status: c.status, campaign_type: c.campaign_type,
                            a: metrics(c), b: { cost: 0, clicks: 0, impressions: 0, conversions: 0 },
                        };
                    });
                    bCamps.forEach((c) => {
                        const id = c.campaign_id || c.id;
                        if (!byId[id]) {
                            byId[id] = {
                                practiceId: pid, practiceName: pname,
                                campaign_id: id, name: c.name, status: c.status, campaign_type: c.campaign_type,
                                a: { cost: 0, clicks: 0, impressions: 0, conversions: 0 }, b: metrics(c),
                            };
                        } else {
                            byId[id].b = metrics(c);
                        }
                    });
                    rows.push(...Object.values(byId));
                } catch (_) { /* skip practices that error / have no GA */ }
            }));
            dispatch(slice.actions.setCrossPracticeCampaignCompare(rows));
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to load campaign comparison' }));
        } finally {
            dispatch(slice.actions.setCrossPracticeCampaignCompareLoading(false));
        }
    };
}

export function fetchCrossPracticeComparison(params = {}) {
    return async () => {
        if (!hasFeatureAccess('google-ads')) return;
        try {
            dispatch(slice.actions.setCrossPracticeLoading(true));
            const queryParams = new URLSearchParams();
            if (params.period_a_start) queryParams.append('period_a_start', params.period_a_start);
            if (params.period_a_end) queryParams.append('period_a_end', params.period_a_end);
            if (params.period_b_start) queryParams.append('period_b_start', params.period_b_start);
            if (params.period_b_end) queryParams.append('period_b_end', params.period_b_end);
            const queryString = queryParams.toString();
            const url = `/api/v1/google-ads/cross-practice-compare/compare/${queryString ? `?${queryString}` : ''}`;
            const response = await callApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setCrossPractice(response.data));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch cross-practice comparison' }));
        } finally {
            dispatch(slice.actions.setCrossPracticeLoading(false));
        }
    };
}
